import { inject } from '@angular/core';
import { TableLazyLoadEvent } from '@openng/optimus-ui/table';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, filter, map, Observable, switchMap } from 'rxjs';
import { LoggerService } from '../logger/services/logger.service';
import { ObservableState } from './observable-state';

/** Stránkování a řazení jedním sloupcem (`pageAndSort` ve filtru BE, `sortPage` = 'sloupec' | '-sloupec'). */
export interface BrowsePageAndSort {
  start?: number;
  count?: number;
  sortPage?: string;
}

/** Stránkování a řazení více sloupci (`pageAndSortList` ve filtru BE). */
export interface BrowsePageAndSortList {
  start?: number;
  count?: number;
  sortListPage?: string[];
}

/** Společný tvar výsledku vyhledávání z BE (`*VysledekDto`). */
export interface BrowseResult<TRow> {
  content?: TRow[];
  total?: number;
}

/** Minimální stav přehledu; potomek ho může rozšířit (archiv, důvod/účel, …). */
export type BrowseState<TFilter, TResult> = {
  filter: TFilter;
  result: TResult;
  /** Zvýšení vynutí nové vyhledání se stejným filtrem (reloadData). */
  reloadTrigger: number;
};

/**
 * Základ stavové služby přehledu (filtr + stránkovaná tabulka). Drží filtr a výsledek, při změně filtru nebo
 * `reloadTrigger` zavolá `search()`. Potomek dodá `defaultFilter`, `emptyResult`, `search()` a volitelně
 * `filterGuard()` (kdy se nemá hledat), `adjustFilter()` (úprava hodnot z formuláře) a `extraSearchKeys`
 * (další klíče stavu, které spouštějí vyhledání).
 *
 * Potomek v konstruktoru zavolá `initBrowse()` a pak buď `connectResult()` (hledá hned), nebo ho nechá zavolat
 * komponentu až při zobrazení stránky.
 */
export abstract class BrowseStateService<
  TFilter extends object,
  TResult extends BrowseResult<unknown>,
  TState extends BrowseState<TFilter, TResult> = BrowseState<TFilter, TResult>,
  TFormValue extends object = Partial<TFilter>,
> extends ObservableState<TState> {
  protected readonly logger = inject(LoggerService).createLogger(this.constructor.name);

  protected abstract readonly defaultFilter: TFilter;
  protected abstract readonly emptyResult: TResult;

  /** `single` = `filter.pageAndSort` (sortPage: string), `list` = `filter.pageAndSortList` (sortListPage: string[]). */
  protected readonly pagingMode: 'single' | 'list' = 'single';

  /** Další klíče stavu, jejichž změna spouští vyhledání (např. `archiv`, `duvodUcel`). */
  protected readonly extraSearchKeys: (keyof TState)[] = [];

  private resultConnected = false;

  /** Celý stav pro šablonu (`vm$ | async`). */
  readonly vm$: Observable<TState> = this.state$;

  /** Volání BE pro aktuální filtr. */
  protected abstract search(filter: TFilter, state: TState): Observable<TResult>;

  /** Kdy se smí hledat (např. jen s vyplněným důvodem). Výchozí: vždy. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected filterGuard(_state: TState): boolean {
    return true;
  }

  /**
   * Převod hodnot formuláře na filtr BE (např. našeptávač → kód). Výchozí: hodnoty beze změny; `TFormValue`
   * se pak shoduje s `Partial<TFilter>`.
   */
  protected adjustFilter(formValue: TFormValue): Partial<TFilter> {
    return formValue as unknown as Partial<TFilter>;
  }

  /** Inicializace stavu výchozím filtrem a prázdným výsledkem; `extra` doplní vlastní klíče potomka. */
  protected initBrowse(extra: Partial<TState> = {}): void {
    this.initialize({
      filter: this.defaultFilter,
      result: this.emptyResult,
      reloadTrigger: 0,
      ...extra,
    } as TState);
  }

  /**
   * Připojí vyhledávání ke stavu; hned po připojení proběhne první vyhledání. Opakované volání nic nedělá, s
   * `reload` znovu načte data (přehled, který má po návratu na stránku vždy ukázat čerstvá data).
   */
  connectResult(options: { reload?: boolean } = {}): void {
    if (this.resultConnected) {
      if (options.reload) this.reloadData();
      return;
    }
    this.resultConnected = true;

    const result$ = this.onlySelectWhen(['filter', 'reloadTrigger', ...this.extraSearchKeys]).pipe(
      distinctUntilChanged(
        (prev, curr) =>
          isEqual(prev.filter, curr.filter) &&
          prev.reloadTrigger === curr.reloadTrigger &&
          this.extraSearchKeys.every((key) => isEqual(prev[key], curr[key]))
      ),
      filter((state) => this.filterGuard(state)),
      // switchMap: odpověď na starší filtr (rychlé stránkování) nepřepíše novější
      switchMap((state) => {
        this.logger.debug('search', state.filter);
        return this.handleErrors(this.search(state.filter, state), this.emptyResult);
      })
    );

    this.connect({ result: result$ } as Partial<{ [P in keyof TState]: Observable<TState[P]> }>);
  }

  /**
   * Další části stavu nastavované z formuláře spolu s filtrem (např. důvod/účel vyhledávání, který BE dostává mimo
   * filtr). Výchozí: nic.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected stateFromForm(_formValue: TFormValue): Partial<TState> {
    return {};
  }

  /** Nový filtr z formuláře: stránkování od začátku, řazení zůstává. */
  findWithNewFilter(formValue: TFormValue = {} as TFormValue): void {
    this.patch({
      ...this.stateFromForm(formValue),
      // stránkování až nakonec – formulář může mít vlastní (prázdné) pole pageAndSort
      filter: { ...this.snapshot.filter, ...this.adjustFilter(formValue), ...this.firstPage(this.snapshot.filter) },
    } as Partial<TState>);
  }

  /** Našeptávač filtru: seznam z odpovědi BE (`pick`), při chybě prázdný. Požadavek posílat se `SKIP_LOADING_CONTEXT`. */
  protected naseptej<R, T>(request: Observable<R>, pick: (res: R) => T[] | undefined): Observable<T[]> {
    return this.handleErrors(request.pipe(map((res) => pick(res) ?? [])), []);
  }

  /** Smazání kritérií: totéž co nový (prázdný) filtr. */
  findWithClearFilter(formValue: TFormValue = {} as TFormValue): void {
    this.findWithNewFilter(formValue);
  }

  /** Částečná změna už převedeného filtru (typicky stránkování/řazení z tabulky). */
  findWithParcialFilter(partialFilter: Partial<TFilter> = {}): void {
    this.patch({ filter: { ...this.snapshot.filter, ...partialFilter } } as Partial<TState>);
  }

  /** Stránkování a řazení z lazy load události PrimeNG tabulky. */
  onLazyLoad(evt: TableLazyLoadEvent): void {
    const page = { start: evt.first, count: evt.rows || undefined };
    const partial =
      this.pagingMode === 'list'
        ? { pageAndSortList: { ...page, sortListPage: this.sortListFromEvent(evt) } }
        : { pageAndSort: { ...page, sortPage: this.sortFromEvent(evt) } };
    this.findWithParcialFilter(partial as unknown as Partial<TFilter>);
  }

  reloadData(): void {
    this.patch({ reloadTrigger: this.snapshot.reloadTrigger + 1 } as Partial<TState>);
  }

  clearResults(): void {
    this.patch({ result: this.emptyResult } as Partial<TState>);
  }

  /** Řazení jedním sloupcem z události tabulky ('' = bez řazení). */
  protected sortFromEvent(evt: TableLazyLoadEvent): string {
    const field = Array.isArray(evt.sortField) ? evt.sortField[0] : evt.sortField;
    return field ? ((evt.sortOrder ?? 1) < 0 ? '-' : '') + field : '';
  }

  /** Řazení více sloupci z události tabulky. */
  protected sortListFromEvent(evt: TableLazyLoadEvent): string[] {
    if (evt.multiSortMeta?.length) {
      return evt.multiSortMeta.map((meta) => (meta.order < 0 ? '-' : '') + meta.field);
    }
    const single = this.sortFromEvent(evt);
    return single ? [single] : [];
  }

  /** Stránkování filtru vrácené na první stránku (po změně kritérií); řazení a počet řádků zůstávají. */
  private firstPage(current: TFilter): Partial<TFilter> {
    const key = this.pagingMode === 'list' ? 'pageAndSortList' : 'pageAndSort';
    const paging = (current as Record<string, unknown>)[key] as object | undefined;
    return { [key]: { ...paging, start: 0 } } as Partial<TFilter>;
  }
}
